import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { appointmentMoveSteps } from '../bookingSteps';
import { PatientBookingConfirmation } from '../components/PatientBookingConfirmation';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import { type CalendarAppointment } from '../appointmentCalendar';

interface Appointment extends CalendarAppointment {
  id: string;
  when: string;
  type?: string;
  procedureId?: string;
  procedureIds?: string[];
  procedureName?: string;
  procedureType?: string;
  practitionerId?: string;
  practitionerIds?: string[];
  practitionersIds?: string[];
  practitionerName?: string;
}

interface Procedure {
  id: string;
  name: string;
}

const asText = (value: unknown) => typeof value === 'string' ? value.trim() : '';
const firstId = (value: unknown) => Array.isArray(value) ? asText(value.find(item => asText(item))) : '';

const readStoredMovedAppointment = (): Appointment | null => {
  try {
    const parsed = JSON.parse(localStorage.getItem('lastMovedAppointment') ?? 'null');
    return parsed && typeof parsed === 'object' ? parsed as Appointment : null;
  } catch {
    return null;
  }
};

const readCachedProcedure = (id: string): Procedure | null => {
  try {
    const cached = JSON.parse(localStorage.getItem('procedureCache') ?? '{}')?.[id];
    return cached?.id === id && asText(cached.name) ? cached as Procedure : null;
  } catch {
    return null;
  }
};

const formatProcedureName = (name: string) => {
  if (!name) return 'Behandlung';
  const translated = t(name as TranslationKey) || name;
  return translated.charAt(0).toUpperCase() + translated.slice(1);
};

const configureAppointmentMoveConfHeader = () => {
  if (localStorage.getItem('headerPhase') !== '3') {
    localStorage.setItem('headerPhase', '3');
  }
};

export function AppointmentMoveConfPage() {
  configureAppointmentMoveConfHeader();

  const navigate = useNavigate();
  const location = useLocation();
  const appointmentFromState = (location.state as { appointment?: Appointment } | null)?.appointment;
  const appointment = useMemo(() => appointmentFromState ?? readStoredMovedAppointment(), [appointmentFromState]);
  const validAppointment = Boolean(asText(appointment?.id) && asText(appointment?.when)
    && !Number.isNaN(new Date(appointment!.when).getTime()));

  // The confirmed response takes precedence over later selections in local storage.
  const matchesSelection = asText(appointment?.id) === localStorage.getItem('selectedAppointmentId');
  const procedureId = firstId(appointment?.procedureIds) || asText(appointment?.procedureId)
    || (matchesSelection ? asText(localStorage.getItem('selectedProcedureId')) : '');
  const practitionerId = firstId(appointment?.practitionerIds) || firstId(appointment?.practitionersIds)
    || asText(appointment?.practitionerId)
    || (matchesSelection ? asText(localStorage.getItem('selectedPractitionerId')) : '');
  const suppliedPractitionerName = asText(appointment?.practitionerName);
  const cachedProcedure = useMemo(() => readCachedProcedure(procedureId), [procedureId]);
  const [loadedProcedure, setLoadedProcedure] = useState<Procedure | null>(null);
  const [loadedPractitioner, setLoadedPractitioner] = useState<{ id: string; name: string } | null>(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!validAppointment || !token) return;
    const controller = new AbortController();
    const options = { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal };

    const loadProcedure = async () => {
      if (!procedureId || cachedProcedure) return;
      try {
        const response = await fetch(`${API_BASE_URL}/procedures/${encodeURIComponent(procedureId)}`, options);
        if (!response.ok) return;
        const payload = await response.json();
        if (!controller.signal.aborted && payload?.id === procedureId && asText(payload.name)) {
          setLoadedProcedure({ id: procedureId, name: asText(payload.name) });
        }
      } catch (error) {
        if (!controller.signal.aborted) console.error('Failed to load confirmed treatment', error);
      }
    };

    const loadPractitioner = async () => {
      if (!practitionerId || suppliedPractitionerName) return;
      try {
        const response = await fetch(`${API_BASE_URL}/practitioners/titled-name/${encodeURIComponent(practitionerId)}`, options);
        if (!response.ok) return;
        const payload = response.headers.get('content-type')?.includes('application/json')
          ? await response.json() : await response.text();
        const name = asText(payload) || asText(payload?.titledName) || asText(payload?.name) || asText(payload?.fullName);
        if (!controller.signal.aborted) setLoadedPractitioner({ id: practitionerId, name });
      } catch (error) {
        if (!controller.signal.aborted) console.error('Failed to load confirmed practitioner', error);
      }
    };

    void loadProcedure();
    void loadPractitioner();
    return () => controller.abort();
  }, [validAppointment, token, procedureId, practitionerId, cachedProcedure, suppliedPractitionerName]);

  const handleOverview = () => navigate('/appointments');

  if (!validAppointment || !appointment) {
    return (
      <div className="portal-page-surface text-slate-900">
        <Header compact steps={appointmentMoveSteps} phase={3} />
        <div className="portal-page-content">
          <div className="rounded-3xl bg-white px-8 py-12 text-center shadow-[0_25px_55px_rgba(19,19,19,0.08)]">
            <h1 className="text-3xl font-semibold text-slate-900">Kein Termin gefunden</h1>
            <p className="mx-auto mt-3 max-w-lg text-sm text-slate-500">
              Die verschobenen Termindaten sind nicht verfuegbar.
            </p>
            <button
              onClick={handleOverview}
              className="mt-8 rounded-xl bg-[#cbb58a] px-6 py-4 text-sm font-semibold text-white shadow-[0_15px_25px_rgba(203,181,138,0.35)] transition hover:bg-[#bca477]"
            >
              Zu meinen Terminen
            </button>
          </div>
        </div>
      </div>
    );
  }

  const procedure = cachedProcedure || (loadedProcedure?.id === procedureId ? loadedProcedure : null);
  const procedureType = asText(appointment.procedureType) || procedure?.name || asText(appointment.procedureName) || asText(appointment.type);
  const procedureName = formatProcedureName(asText(appointment.procedureName) || procedure?.name
    || asText(appointment.procedureType) || asText(appointment.type));
  const practitionerName = suppliedPractitionerName
    || (loadedPractitioner?.id === practitionerId ? loadedPractitioner.name : '') || 'Unbekannt';
  const date = new Date(appointment.when);

  return (
    <PatientBookingConfirmation
      heading="Termin erfolgreich verschoben"
      description="Ihr Termin wurde aktualisiert. Nachfolgend sehen Sie die neuen Details."
      steps={appointmentMoveSteps}
      phase={3}
      procedureId={procedureId}
      procedureType={procedureType}
      procedureName={procedureName}
      practitionerName={practitionerName}
      date={date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
      time={date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      calendarAppointment={appointment}
      onEditAppointment={handleOverview}
      onOverview={handleOverview}
    />
  );
}
